import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import ProductInfoContainer from 'pricing-pages-lib/js/components/ProductInfoContainer.tsx';
import { createToObject } from 'pricing-pages-lib/utils/queryParamUtils';
import { sortProductLines } from 'pricing-pages-lib/utils/sortingUtils';
import { PPUIPageHeader } from 'pricing-pages-ui-components/semantic/PageHeader';
import { PageSection } from 'pricing-pages-ui-components/semantic/PageSection';
import { useQueryParamTerm, useQueryParamsEffect } from 'pricing-pages-ui/utils/urls/appQueryParamUtils';
import { useEffect } from 'react';
import { NavMarker } from 'react-rhumb';
import { Redirect } from 'react-router-dom';
import { MARKETING_PLUS_SUITE } from 'self-service-api/constants/BundleTypes';
import { MARKETING_PLUS_PROFESSIONAL } from 'self-service-api/constants/UpgradeProducts';
import BannerSection from '../components/banners/BannerSection';
import IntegrationsSection from '../components/common/IntegrationsSection';
import PhoneSupport from '../components/common/PhoneSupport';
import PhoneSupportFooter from '../components/common/PhoneSupportFooter';
import ProductPageSubtitle from '../components/common/ProductPageSubtitle';
import ProductPageTitle from '../components/common/ProductPageTitle';
import ValuePropsSection from '../components/common/ValuePropsSection';
import CodeSplitEnvWrapper from '../components/common/env/CodeSplitEnvWrapper';
import FailedLoadBanner from '../components/common/loading/FailedLoadBanner';
import FallbackErrorBanner from '../components/common/loading/FallbackErrorBanner';
import SkeletonLoadWrapper from '../components/common/loading/SkeletonLoadWrapper';
import { withErrorBoundary } from '../components/error-boundary/withErrorBoundary';
import GenericBundleFeatureTable from '../components/feature-table/suite-feature-table/GenericBundleFeatureTable';
import HeaderLayout from '../components/layout/HeaderLayout';
import MarketingPlusTiles from '../components/marketing-plus/MarketingPlusTiles';
import { MARKETING_PLUS_PAGE } from '../constants/ErrorBoundaryNames';
import { OWNERSHIP_CHECK_FAILED } from '../constants/ErrorCauses';
import { NavItemEnum } from '../constants/routing/NavItems';
import { ENTERPRISE } from '../constants/routing/Tabs';
import { SCREENS } from '../constants/tracking/Screens';
import { MARKETING_PLUS_PAGE_FEATURE_TABLE } from '../constants/tracking/TrackingSources';
import { useAnchorLinkScroll } from '../hooks/htmlElementHooks';
import { useGlobalLoading } from '../hooks/loadingHooks';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useRedirectIfIneligible } from '../hooks/useRedirectIfIneligible';
import { useTrackRetail } from '../hooks/useTrackRetail';
import ErrorPage from '../pages/ErrorPage';
import { useAppSelector } from '../reducers';
import { isUngatedForMarketingPlusSelector } from '../selectors/GatingSelectors';
import { hasOnlyOwnershipCheckFailedSelector, hasPricingAndPurchasingFailedSelector } from '../selectors/LoadingStateSelectors';
import { bundledProductLinesForApiNameSelector } from '../selectors/ProductConfigsSelectors';
import { bundleProductsForBundleTypeSelector } from '../selectors/ProductsSelectors';
import { trackPageView } from '../utils/tracking/trackingEventUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MarketingPlusPage = () => {
  useAnchorLinkScroll();
  useTrackRetail();
  useDocumentTitle('marketing-plus');
  const isAppReady = useGlobalLoading();
  const hasPricingAndPurchasingFailed = useAppSelector(hasPricingAndPurchasingFailedSelector);
  const hasOnlyOwnershipCheckFailed = useAppSelector(hasOnlyOwnershipCheckFailedSelector);
  const screen = SCREENS.MARKETING_PLUS;
  const navItem = NavItemEnum.MARKETING_PLUS;
  const isUngatedForMarketingPlus = useAppSelector(isUngatedForMarketingPlusSelector);
  useRedirectIfIneligible({
    condition: () => !isUngatedForMarketingPlus
  });
  useEffect(() => {
    if (!isAppReady) return;
    trackPageView({
      screen
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAppReady]);
  const highlightedProducts = useAppSelector(state => {
    if (hasPricingAndPurchasingFailed) return [];
    return bundleProductsForBundleTypeSelector(state, {
      bundleType: MARKETING_PLUS_SUITE
    }).map(({
      apiName
    }) => apiName);
  });
  const productLinesFromBundle = useAppSelector(state => {
    return sortProductLines(bundledProductLinesForApiNameSelector(state, {
      apiName: highlightedProducts[0]
    }));
  });
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  if (hasPricingAndPurchasingFailed) {
    if (hasOnlyOwnershipCheckFailed) {
      return /*#__PURE__*/_jsx(ErrorPage, {
        errorCause: OWNERSHIP_CHECK_FAILED
      });
    }
  }
  if (!isAppReady) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      minHeight: 200,
      size: "md",
      grow: true
    });
  }
  return /*#__PURE__*/_jsxs("div", {
    "data-selenium-id": "page-success",
    children: [isAppReady && /*#__PURE__*/_jsx(NavMarker, {
      name: "MARKETING_PLUS_PAGE"
    }), /*#__PURE__*/_jsx(HeaderLayout, {
      selectedNavItem: navItem,
      children: /*#__PURE__*/_jsxs(ProductInfoContainer, {
        children: [/*#__PURE__*/_jsx(BannerSection, {
          screen: screen
        }), /*#__PURE__*/_jsx(FailedLoadBanner, {
          hasFailed: hasPricingAndPurchasingFailed
        }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(PPUIPageHeader, {
          children: [/*#__PURE__*/_jsx(ProductPageTitle, {
            nonProductIdentifier: navItem
          }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
            nonProductIdentifier: navItem
          })]
        }), /*#__PURE__*/_jsx(MarketingPlusTiles, {
          screen: screen
        }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
          isLoading: !isAppReady,
          heights: [40],
          widths: ['100%'],
          children: /*#__PURE__*/_jsx(PageSection, {
            children: /*#__PURE__*/_jsx(GenericBundleFeatureTable, {
              apiNames: highlightedProducts,
              productLinesFromBundle: productLinesFromBundle,
              screen: screen,
              trackingSource: MARKETING_PLUS_PAGE_FEATURE_TABLE
            }, screen)
          })
        }), /*#__PURE__*/_jsx(PageSection, {
          children: /*#__PURE__*/_jsx(PhoneSupport, {
            phoneSourceId: "marketingplus-middle",
            screen: screen,
            upgradeProduct: MARKETING_PLUS_PROFESSIONAL
          })
        }), /*#__PURE__*/_jsx(IntegrationsSection, {}), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
          publicComponentLoader: () => import( /* webpackChunkName: "public-chunk" */`pricing-pages-ui/components/company-proof/CompanyProof`),
          publicComponentProps: {
            tab: ENTERPRISE,
            screen
          }
        }), /*#__PURE__*/_jsx(ValuePropsSection, {}), /*#__PURE__*/_jsx(PhoneSupportFooter, {
          phoneSourceId: "marketingplus-footer",
          screen: screen,
          upgradeProduct: MARKETING_PLUS_PROFESSIONAL
        })]
      })
    })]
  });
};
export default withErrorBoundary(MarketingPlusPage, {
  fallback: /*#__PURE__*/_jsx(Redirect, {
    to: createToObject('/suite')
  }),
  errorBoundaryName: MARKETING_PLUS_PAGE
});